import { debounce, lazySelector } from '@phx-styleguides/core';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export class IntroBlock {
  public el: HTMLElement;
  private animatedHeader: HTMLElement | null = null;
  private stickySection: HTMLElement | null = null;
  private infos: HTMLElement | null = null;
  private mobileHeading: HTMLElement | null = null;
  private media: HTMLElement | null = null;
  private skipButton: HTMLElement | null = null;

  private resizeObserver: ResizeObserver | null = null;
  private scrollListener: (() => void) | null = null;
  private skipButtonClickListener: (() => void) | null = null;
  private stickySectionTop = 0;
  private timeline: gsap.core.Timeline | null = null;

  constructor(el: string | HTMLElement) {
    this.el = lazySelector(el);

    this.init();
  }

  init() {
    this.stickySection = this.el.querySelector('.intro-block__sticky-section');
    this.infos = this.el.querySelector('.intro-block__infos');
    this.animatedHeader = this.el.querySelector('.intro-block__animated-header');
    this.media = this.el.querySelector('.intro-block__media');
    this.mobileHeading = this.el.querySelector('.intro-block__mobile-header');
    this.skipButton = this.el.querySelector('.intro-block__skip-button');

    if (this.animatedHeader && this.stickySection) {
      this.animateHeader();
    }

    if (this.stickySection) {
      this.stickySectionTop = this.stickySection.getBoundingClientRect().top + window.pageYOffset;
    }

    if (this.skipButton) {
      this.skipButtonClickListener = this.onSkipButtonClick.bind(this);
      this.skipButton.addEventListener('click', this.skipButtonClickListener);
    }

    this.setupScrollListener();
    this.onScroll();

    if (this.infos) {
      this.updateMobileOffset();
      this.setupResizeObserver();
    }
  }

  private animateHeader() {
    if (!this.animatedHeader || !this.stickySection) return;

    if (this.timeline) {
      this.timeline.kill();
      ScrollTrigger.refresh();
    }

    const navHeight = getComputedStyle(document.documentElement).getPropertyValue('--comp-navigation-height');

    // Calculate the scale of the animated header based on its width and the window width. This is done so that the initial width of the media is the same as the hero section above.
    const animatedHeaderStyle = getComputedStyle(this.animatedHeader);
    const paddingLeft = parseFloat(animatedHeaderStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(animatedHeaderStyle.paddingRight) || 0;
    const animatedHeaderWidth = this.animatedHeader.getBoundingClientRect().width - paddingLeft - paddingRight;
    const animatedHeaderScale = animatedHeaderWidth / window.innerWidth;

    this.timeline = gsap.timeline({
      force3D: true,
      scrollTrigger: {
        trigger: this.stickySection,
        // since there's a negative margin-top of 10vh, we need to add it to the start position
        start: () => `top+=${innerHeight * 0.1}px bottom`,
        end: `top top+=${navHeight}`,
        scrub: true,
      },
      onUpdate: function () {
        const shouldShowTitle = this.progress() >= 0.9;

        gsap.to('.intro-block__title', {
          opacity: shouldShowTitle ? 1 : 0,
          duration: 0.2,
        });
      },
    });
    gsap.set(this.stickySection, { scale: animatedHeaderScale });
    gsap.set(this.animatedHeader, { y: '0%' });
    gsap.set(this.media, { y: '-20%' });

    this.timeline
      .to(this.media, { y: 0, ease: 'none' }, '<')
      .to(this.stickySection, { scale: 1, ease: 'none' }, '<')
      .to(this.animatedHeader, { y: '-20%', ease: 'none' }, '<');
  }

  private onSkipButtonClick() {
    if (this.infos) {
      this.infos.scrollIntoView({ behavior: 'smooth' });
    }
  }

  private onScroll() {
    const scrolledFromStickyTop = window.scrollY - this.stickySectionTop;
    const hasScrolled = scrolledFromStickyTop >= 200;

    if (this.infos) {
      this.infos.classList.toggle('has-scrolled', hasScrolled);

      const infosTop = this.infos.getBoundingClientRect().top;
      const isInfosAtTop = infosTop <= 0;

      this.el.classList.toggle('video-hidden', isInfosAtTop);
    }

    this.el.classList.toggle('has-scrolled', hasScrolled);
  }

  private updateMobileOffset() {
    if (!this.mobileHeading || !this.el) return;

    const elTop = this.mobileHeading.getBoundingClientRect().top;
    const headingBottom = this.mobileHeading.getBoundingClientRect().bottom;
    const heightDifference = headingBottom - elTop;

    this.el.style.setProperty('--mobile-heading-offset-height', `${heightDifference}px`);
  }

  private setupScrollListener() {
    this.scrollListener = this.onScroll.bind(this);

    window.addEventListener('scroll', this.scrollListener);
  }

  private setupResizeObserver() {
    if (!this.infos) return;

    this.resizeObserver = new ResizeObserver(
      debounce(() => {
        this.updateMobileOffset();
        this.onScroll();
        this.animateHeader();
      }, 50),
    );

    this.resizeObserver.observe(this.infos);
  }

  public destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
    }

    if (this.skipButton && this.skipButtonClickListener) {
      this.skipButton.removeEventListener('click', this.skipButtonClickListener);
    }
  }
}
